"use client";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { FiAlertTriangle, FiCheck } from "react-icons/fi";
import { motion, useReducedMotion } from "framer-motion";

import { Transition } from "@components/ui/motion";
import { LogoGridProps } from "@src/types/section";

import ContentfulImage from "./image";

const EASE = [0.17, 0.55, 0.55, 1] as const;

const LogoGrid = ({ section }: { section: LogoGridProps }) => {
	const shouldReduceMotion = useReducedMotion();

	if (!section?.listCollection?.items?.length) return null;

	return (
		<div className="content-container">
			<Transition type="SlideUp">
				<h2 className="py-20 text-center">{section?.name}</h2>
			</Transition>
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 justify-items-center gap-12 items-center justify-center px-8">
				{section?.listCollection?.items?.map((partner, i) => (
					<motion.div
						key={JSON.stringify(partner)}
						initial={shouldReduceMotion ? false : { opacity: 0, y: 24 }}
						whileInView={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
						viewport={{ once: true, margin: "-10%" }}
						transition={{ duration: 0.5, delay: (i % 3) * 0.08, ease: EASE }}
					>
						<ContentfulImage
							src={partner?.logo?.url}
							alt={partner.name}
							width={300}
							height={300}
							style={{ maxWidth: "100%", height: "auto" }}
						/>
					</motion.div>
				))}
			</div>
			<Transition type="SlideUp" delay={0.1}>
				<div className="flex flex-col lg:flex-row gap-8 items-center max-w-3xl px-4 lg:px-0 mx-auto my-18">
					{section?.subicon ? (
						<div className="flex flex-row gap-4 items-center">{getSubIcon(section?.subicon)}</div>
					) : null}
					<div className="prose prose-p:mt-1 prose-headings:text-white prose-a:text-white max-w-none">
						{section?.subtitle ? <h3 className="text-left mb-0">{section.subtitle}</h3> : null}
						{section?.subtext && (
							<div className="text-slate-50">{documentToReactComponents(section?.subtext?.json)}</div>
						)}
					</div>
				</div>
			</Transition>
		</div>
	);
};

const getSubIcon = (icon: string) => {
	switch (icon) {
		case "check":
			return <FiCheck size={84} />;
		case "warning-triangle":
			return <FiAlertTriangle size={84} className="text-red-500" />;
		default:
			return null;
	}
};

export default LogoGrid;
